// src/lib/production.js
// Recording that a branch MADE something for its own shelf, and what it cost.
// Issue #515, Session 79.8, built against gate:
// docs/gates/Stede_Production_Gate_515.html — decisions D31-D37, approved 79.7.
//
// ── What this is, in plain words ──
//
// Houston builds 300 stakes for its own rack, so that when Dallas asks for a
// hundred they can go out the same day instead of being cut to order. That is
// an ordinary thing a factory does every week.
//
// Stede has recorded it correctly in the database since Session 79.6 — and only
// if somebody calls the database by hand. There was no button. A function in
// the schema that nobody can reach is worse than one that does not exist,
// because it reads as finished.
//
// ── Its own file rather than more of branchTransfers.js, and why ──
//
// A production run IS NOT A TRANSFER. Colby's ruling at 79.6 is the whole
// reason this exists separately: "ship 100 from reserve and make 100 to
// replace" as one action would have the transfer document record production
// that has not happened yet, so Houston's count would read as though the
// replacements already existed. A replacement run is its own event.
//
// The two do meet in one place — the To make queue shows both, because a
// fabricator's day is one queue rather than two screens — and that merge is
// `productionRows()` below. It takes both lists as arguments rather than
// fetching either, so the page owns the loading and this file stays testable.
//
// ── The rule inherited from the transfer build, restated because it governs ──
//
// WHO MAKES A THING COMES FROM THE CATEGORY TREE, NEVER FROM THE ITEM'S VENDOR.
// Session 79.5 disproved the vendor rule on live data: Dibond White, Dibond
// Black and MDO all carry Houston vendor links and Houston makes none of them.
// This file walks the tree with `resolveFulfilment()` for exactly that reason,
// and this session's own migration walked into the vendor rule once more in its
// backfill before the returned rows gave it away.

import { supabase } from './supabase';
import { resolveFulfilment, isOwnShelf, agreedLines } from './branchTransfers';
import { canSetProductionCost } from './permissions';
import { cutOff } from './readLimit';

// ─────────────────────────────────────────────────────────────────────────────
// PURE FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Does this branch make this item? Gate D31, and the tree is the authority.
//
// Note what this does NOT answer: whether the branch is ALLOWED to record
// making it. `record_production()` deliberately declines that check — a branch
// that cuts something unusual once should be able to say so — and refuses only
// when NOBODY makes the thing. This predicate is what decides whether the item
// appears in a branch's queue unasked, which is a different and softer question.
export function branchMakes(categories, item, branchId) {
  if (!item || branchId == null) return false;
  const f = resolveFulfilment(categories, item.category_id);
  return f.mode === 'make' && String(f.branchId) === String(branchId);
}

// Everything this branch makes, whatever the count — the list behind BOTH
// doorways into a production run.
//
// ── WHY THIS EXISTS, AND IT IS A DEFECT COLBY FOUND BY LOOKING AT THE SCREEN ──
//
// The first build gave `record_production()` exactly ONE caller: the button on
// a reserve row in the To make queue. A reserve row appears only when an item
// is AT OR BELOW its reorder point — and no Houston item has a reorder point
// set, so the button lived on a row that never rendered. HOUSTON COULD RECORD
// NOTHING AT ALL.
//
// That is #515's own defect one layer up: a thing that reads as finished and
// cannot be reached. The function had a caller and the caller had no door.
//
// Colby, sitting in front of the built screen: "houston will need a way to
// record production that's not tied to any specific transfer request." His
// slow-season case from 79.6 is the plain proof — deciding to build 300 frames
// ahead of demand has nothing to do with anything being low.
//
// THE QUEUE'S REORDER CONDITION IS STILL RIGHT AND MUST NOT BE LOOSENED. That
// list answers "what should we build", so it should only carry what is running
// short; #518 is what happens when a list carries everything. The mistake was
// treating a WORK LIST as the only door.
//
// AND WITHOUT A DOOR, PEOPLE USE ADJUST — which writes a stock correction with
// no cost and no production against it. That is precisely the record #515
// exists to stop, and it would look correct to everybody.
//
// `branchMakes` rather than "is made by anyone", deliberately. The shipped
// function declines to check WHICH branch, and its own comment says why: "the
// screen only offers this where it makes sense; the database does not pretend
// to know." So Dallas cutting something unusual once is still possible through
// the database, and is not offered as a button on every stake at Dallas —
// which is the undesigned permission gate D35 warns about.
export function makeableAtBranch({ items = [], categories = [], branchId }) {
  if (branchId == null) return [];
  return items
    .filter(i => branchMakes(categories, i, branchId))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// Reserve rows — gate screen 1, callout B.
//
// THIS IS THE ONLY PLACE IN STEDE WHERE A REORDER POINT PRODUCES WORK RATHER
// THAN A PURCHASE. Everywhere else a low count means somebody should buy
// something. Here it means somebody should build something, because the branch
// looking at the row is the branch that makes it.
//
// An item with NO reorder point is deliberately excluded rather than treated as
// zero. #357 records the opposite decision for the low-stock lists and it does
// not transfer: there, a thing at zero is visibly a problem whether or not
// anybody set a threshold. Here the list is a work queue, and putting every
// item a branch makes on it — at any quantity, forever — is how a queue becomes
// wallpaper. #518 is the same lesson one screen over: Stock Home rendered 54
// blocks for 32 items and Colby could not use it.
export function reserveCandidates({ items = [], stock = [], categories = [], branchId }) {
  if (branchId == null) return [];

  const stockFor = new Map(
    stock
      .filter(s => String(s.branch_id) === String(branchId))
      .map(s => [String(s.item_id), s]));

  return items
    .filter(i => branchMakes(categories, i, branchId))
    .map(i => {
      const s = stockFor.get(String(i.id));
      const onHand = num(s?.qty_on_hand) ?? 0;
      const reorder = num(s?.reorder_point);
      return { item: i, qtyOnHand: onHand, reorderPoint: reorder };
    })
    .filter(r => r.reorderPoint != null && r.qtyOnHand <= r.reorderPoint)
    // Furthest below its own threshold first. Compared as a RATIO rather than a
    // difference, so 2-of-10 outranks 90-of-100 — the first is nearly out and
    // the second is a rounding error on a big number.
    .sort((a, b) => {
      const ra = a.reorderPoint > 0 ? a.qtyOnHand / a.reorderPoint : 0;
      const rb = b.reorderPoint > 0 ? b.qtyOnHand / b.reorderPoint : 0;
      if (ra !== rb) return ra - rb;
      return String(a.item.name || '').localeCompare(String(b.item.name || ''));
    });
}

// The To make queue, both kinds in one list — gate screen 1, callout A.
//
// ORDER-BACKED ROWS SORT FIRST, ALWAYS, and that is a decision rather than a
// convenience: somebody at another branch is waiting on a promised date, and a
// reserve run is work the branch chose for itself. A reserve row that pushed a
// promised order down the list would be the queue actively misleading the
// person reading it.
//
// `orders` comes from productionQueue() in branchTransfers.js and is already
// oldest-first. It is passed in rather than computed here so that the admin
// case — every branch's work, not just your own — stays the page's problem.
//
// The two kinds are tagged and kept in ONE array here on purpose, unlike
// transfers and material requests, which TransfersPage holds separately and
// must never merge. The difference is that nothing downstream of this list
// applies a permission rule to a row: it is read to draw a list, and both kinds
// route to the same button. The transfer/request separation exists precisely
// because canApprove() silently returns the wrong answer for the wrong shape.
// ORDER ROWS CARRY NO "RECORD PRODUCTION" BUTTON AT 79.8, AND THE GATE'S OWN
// SCREEN 1 DRAWS ONE. THIS IS A DELIBERATE OMISSION, NOT AN OVERSIGHT — READ
// THIS BEFORE ADDING IT.
//
// The gate's queue shows "0 of 100 · Record production" on an order-backed row,
// which reads as progress against somebody's order. Built naively that DOUBLES
// HOUSTON'S STOCK, and both halves look honest to the person doing it:
//
//   1. Houston builds 100 frames for Dallas's order and records it here.
//      The rack goes UP by 100. So far so good — they are really there.
//   2. Houston ships them and the ship sheet asks how the line was filled.
//      The truthful answer is "we made them", so they answer `make`.
//   3. `ship_branch_transfer()` writes a production_in AND a transfer_out for
//      a `make` line — it assumes the goods were made AT SHIP TIME and never
//      touched the rack. The rack goes +100 then -100.
//
// Net: +100 frames that do not exist, from two correct answers. The right
// answer at step 2 is `stock`, because by then they ARE on the rack, and
// nothing on the screen tells anybody that.
//
// Fixing it properly means either linking a production row to the transfer it
// was built for, or making the ship sheet answer `sourced` from what is
// actually on the shelf. Both change `ship_branch_transfer()`, which is #371's
// certified path, and neither is what #515 was logged for — its defect is that
// `record_production()` has no caller at all, and the RESERVE half is that
// caller in full.
//
// So the queue shows both kinds, which is callout A's real point — a
// fabricator's day is one list — and the button appears only where it cannot
// double-count. Raised with Colby at 79.8's close.
//
// #580 part (a), gate 553 D24 -- THREE KINDS NOW, IN THIS ORDER: other
// branches' orders, then requests for OUR OWN SHELF, then reserve rows nobody
// asked for, oldest first within each. A branch restocking itself never quietly
// jumps ahead of Dallas waiting on a truck. And WHILE A REQUEST FOR OUR OWN
// SHELF IS OPEN, THAT ITEM'S RESERVE ROW STEPS ASIDE -- otherwise the same
// frames appear twice, and a run recorded against the reserve row lands on the
// shelf while the request stays open asking for them again.
//
// `openOwnShelf` is every open request this branch raised for its own shelf,
// awaiting confirmation or not: an unconfirmed one is not on this list yet, but
// it is still the answer to "is anybody already dealing with these".
export function productionRows({ orders = [], reserve = [], openOwnShelf = [] } = {}) {
  const asked = new Set(
    (Array.isArray(openOwnShelf) ? openOwnShelf : [])
      .filter(isOwnShelf)
      .flatMap(t => agreedLines(t.branch_transfer_items || t.items || []))
      .map(l => String(l.item_id)));
  const others = orders.filter(t => !isOwnShelf(t));
  const ours = orders.filter(t => isOwnShelf(t));
  return [
    ...others.map(t => ({ kind: 'order', key: `t${t.id}`, transfer: t })),
    ...ours.map(t => ({ kind: 'order', own: true, key: `t${t.id}`, transfer: t })),
    ...reserve
      .filter(r => !asked.has(String(r.item?.id)))
      .map(r => ({ kind: 'reserve', key: `i${r.item.id}`, ...r })),
  ];
}

// What a run adds to the shelf, in money — gate screen 2, callout E.
//
// Shown BEFORE saving, never after. Somebody recording 300 stakes should see
// $1,230 leave the abstract and become a number they can sanity-check against
// what they know. Returns null rather than 0 when there is no cost to multiply,
// because "$0.00" is a claim and "—" is an honest absence.
export function valueAdded({ qty, unitCost }) {
  const q = num(qty);
  const c = num(unitCost);
  if (q == null || c == null) return null;
  return q * c;
}

// What changing the set cost does to stock already on the shelf — gate screen 2,
// callout F.
//
// STATED IN DOLLARS, NOT IN PRINCIPLE. "This will revalue inventory" is not
// checkable by the person pressing Save. "312 on hand goes from $1,154.40 to
// $1,279.20 — a change of +$124.80" is.
//
// `qtyOnHand` is the total across every branch holding the item, because the
// cost is a property of the ITEM (gate D32, one column) rather than of a
// branch's shelf. A figure that named only one branch's stock would understate
// the change on the exact screen that exists to state it fully.
//
// Returns null when there is nothing to say: no stock on hand anywhere, or no
// change in the number. A revaluation panel that renders "+$0.00" teaches the
// reader to stop reading it.
export function revaluation({ qtyOnHand, oldCost, newCost }) {
  const q = num(qtyOnHand);
  const from = num(oldCost);
  const to = num(newCost);
  if (q == null || q === 0 || to == null) return null;
  const before = (from ?? 0) * q;
  const after = to * q;
  if (before === after) return null;
  return { qtyOnHand: q, before, after, delta: after - before, hadNoCost: from == null };
}

// ─────────────────────────────────────────────────────────────────────────────
// DATABASE OPERATIONS
// ─────────────────────────────────────────────────────────────────────────────

// Record that this branch made something for its own shelf.
//
// NOBODY IS NOTIFIED, and that is gate acceptance line P1 rather than an
// omission. A branch building its own stock is not an event anyone else needs
// to be told about; #500's lesson at 79.1 was that a write-off paged four
// managers including one who runs a different building, and the fix was to
// narrow the audience rather than to add a filter.
//
// The function runs AS THE CALLER (SECURITY INVOKER), which is the enforcement
// rather than a default: the insert rule on the stock ledger ties the row to
// the caller's own branch, so nobody can record production at a branch they are
// not standing in. There is no branch argument here for that reason.
//
// A cost of null is legitimate and is NOT the same as passing zero. Null means
// "use the item's set cost", which the database resolves; zero is a claim that
// the thing was free. The sheet pre-fills the field so this rarely arises, and
// the database no longer depends on the sheet having done so.
//
// #761, Session 79.22b.6c.3 -- IT NAMES THE RECORD IT WROTE, so the answer can
// carry an Undo. `record_production_run()` calls `record_production()`
// unchanged (it refuses exactly what that refuses) and returns {tx_id, item_id,
// qty, qty_on_hand} (20261005_correct_sign_off_and_shelf_run_702_761.sql,
// statement 10). An answer with no record named is not a confirmed write.
export async function recordProduction({ itemId, qty, unitCost = null, notes = null }) {
  const q = num(qty);
  if (q == null || q <= 0) return { error: new Error('how many were made?') };

  const { data, error } = await supabase.rpc('record_production_run', {
    p_item_id: itemId,
    p_qty: q,
    p_unit_cost: unitCost == null || unitCost === '' ? null : num(unitCost),
    p_notes: notes || null,
  });
  if (error) return { error };
  if (data?.tx_id == null) return { error: new Error('That did not save. Nothing was recorded — try again.') };
  return { ok: true, qtyOnHand: num(data.qty_on_hand), txId: data.tx_id, qty: q };
}

// #761, Session 79.22b.6c.3 -- THE UNDO A RUN FOR THE SHELF NEVER HAD (682
// D2(b), callout Z). Only the person who recorded it, at their branch; never a
// sign-off's record; no time limit in the database - the screen offers it only
// while its answer shows. Sends no bell (#694). Returns {tx_id, undone_tx_id,
// item_id, qty, qty_on_hand} (statement 11).
export async function undoRecordProduction(txId) {
  if (txId == null) return { error: new Error('Nothing to undo.') };
  const { data, error } = await supabase.rpc('undo_record_production', { p_tx_id: txId });
  if (error) return { error };
  if (data?.tx_id == null) return { error: new Error('That did not save. Nothing was undone — try again.') };
  return { ok: true, qtyOnHand: num(data.qty_on_hand) };
}

// #761, T4 -- RECORD PRODUCTION'S ANSWER, on To make's card and on the item's
// page: "Recorded 40 Stake Push 24 · Houston's rack is now at 56". The gate's
// F2 words; `qtyText` would add nothing a whole number needs.
export function recordedLine({ qty, itemName, branchName, qtyOnHand }) {
  return `Recorded ${fmtQty(qty)} ${itemName || ''} · ${branchName || 'This branch'}'s rack is now at ${fmtQty(qtyOnHand)}`;
}

// #758, 682 D12 -- the low-shelf row's words beside "Our own rack · 20 on hand".
// reserveCandidates() keeps a row AT or below its reorder point, on purpose;
// the sentence says which: ", at its reorder point of 20" or ", below 20".
export function reorderWords(qtyOnHand, reorderPoint) {
  const point = num(reorderPoint);
  if (point == null) return '';
  return num(qtyOnHand) === point ? `, at its reorder point of ${fmtQty(point)}` : `, below ${fmtQty(point)}`;
}

function fmtQty(n) {
  const v = num(n);
  return v == null ? '—' : String(Number(v.toFixed(2)));
}

// Set what an item costs to make — gate D32, Option A.
//
// THE WRITE IS READ BACK, and that is not defensive padding. #504: the stock
// ledger had row security on with only INSERT and SELECT policies, so every
// UPDATE from the app matched ZERO ROWS — and PostgREST reports zero rows
// changed as SUCCESS with no error. The screen said "Added just now", hid its
// own button, and wrote nothing. `inventory_items` does carry a write policy
// covering every command, checked live before this was written, so this should
// never fire; it costs one round trip and it is the only thing that can tell
// the difference between a write and a silent refusal.
//
// `setBy` is recorded because the gate's screen says "Last changed Jul 2 ·
// Angie" and a name nobody can produce makes that line a lie. Items carried
// over by the migration's backfill deliberately have NO author — the screen
// says the cost has not been set by anyone yet rather than naming somebody who
// did not set it.
export async function setStandardCost({ itemId, cost, setBy }) {
  const c = cost == null || cost === '' ? null : num(cost);
  if (c == null) return { error: new Error('enter what it costs to make') };
  if (c < 0) return { error: new Error('a cost cannot be negative') };

  const { data, error } = await supabase
    .from('inventory_items')
    .update({
      standard_cost: c,
      standard_cost_set_by: setBy || null,
      standard_cost_set_at: new Date().toISOString(),
    })
    .eq('id', itemId)
    .select('id, standard_cost, standard_cost_set_at');

  if (error) return { error };
  if (!data || data.length === 0) {
    return { error: new Error('the cost was not saved — nothing was changed. Tell Colby: this is the #504 shape.') };
  }
  return { ok: true, item: data[0] };
}

// Everything the To make queue needs, in as few round trips as it can be done.
//
// The categories come back whole rather than filtered, because resolveFulfilment
// walks UP the tree and a filtered set breaks the walk at the first ancestor it
// is missing — the kind of fault that produces an empty queue and no error.
//
// #608, its 79.22b.5.6b amendment, built at Session 79.22b.6.2d — A CUT OR
// FAILED READ IS SAID, NEVER PASSED OFF AS THE WHOLE. The database hands back at
// most 1,000 rows and cuts the rest with no error. These three reads asked for
// no count, so past 1,000 active catalog items To make's shelf rows and Record
// something else would have lost sizes with nothing on screen to say so, and a
// failed read looked exactly like "nothing is low". Each read now asks for its
// count, and any read that fails or arrives cut (cutOff(), gate 675 D17) makes
// the whole answer `ok: false` with three EMPTY lists: a part of the catalog is
// never handed on as if it were all of it. Every caller says so in its own way -
// Stock Home's work row is simply absent (fetchTransferWork()), To make says so
// in a line, and the Welding page shows its error state (loadWeldingBoard()).
export async function fetchProductionData(branchId) {
  const [categories, items] = await Promise.all([
    supabase.from('inventory_categories')
      .select('id, name, parent_id, manufactured_at_branch_id, manufacturing_department_id', { count: 'exact' }),
    supabase.from('inventory_items')
      .select('id, name, sku, uom, category_id, standard_cost, standard_cost_set_at, standard_cost_set_by', { count: 'exact' })
      .eq('is_active', true),
  ]);

  const stock = await supabase
    .from('inventory_stock')
    .select('item_id, branch_id, qty_on_hand, reorder_point', { count: 'exact' })
    .eq('branch_id', branchId);

  const reads = [categories, items, stock];
  if (reads.some(r => !r || r.error || cutOff(r))) {
    return { ok: false, cut: reads.some(r => r && !r.error && cutOff(r)), categories: [], items: [], stock: [] };
  }
  return { ok: true, categories: categories.data || [], items: items.data || [], stock: stock.data || [] };
}

// Total on hand across every branch, for the revaluation figure. One row.
export async function totalOnHand(itemId) {
  const { data, error } = await supabase
    .from('inventory_stock')
    .select('qty_on_hand')
    .eq('item_id', itemId);
  if (error) return { error };
  return { ok: true, qty: (data || []).reduce((sum, r) => sum + (num(r.qty_on_hand) ?? 0), 0) };
}

// Who last set an item's cost to make, and what they are — for the lock.
//
// Colby, Session 79.8: "once an executive sets cost to make then even a
// manager shouldn't be able to override without clearence."
//
// Stede already does this for vendor prices — `price_locked` and `locked_by`
// on `vendor_items`, with the save refusing outright on a locked row — so this
// is reaching for an existing pattern rather than inventing one. SAP is the
// same shape: a standard cost is RELEASED rather than typed over.
//
// THE SCREEN HALF ONLY. The database refuses this independently, in
// `enforce_production_cost_actions()`, and that is the half that counts:
// hiding a control is advisory, and this session already found a cost written
// straight past a hidden control. This exists so the person is told WHO to
// ask, which a bare refusal cannot do.
//
// Returns null when nobody is on record — an item nobody has priced, or one
// whose setter has since been deleted. Both mean "not locked", which is the
// right direction: a figure nobody can be asked about should not be frozen.
export async function costSetter(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, role')
    .eq('id', userId)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

// May THIS person change THIS figure? The screen's copy of the database rule.
//
// Written to take the setter rather than fetch one, so it is testable without
// a network and so the page decides when to look the setter up.
export function mayChangeThisCost({ auth, setter }) {
  const { isAdmin, isExecutive } = auth || {};
  if (isAdmin || isExecutive) return { allowed: true, lockedBy: null };
  if (!canSetProductionCost(auth)) return { allowed: false, lockedBy: null };
  if (setter && ['admin', 'executive'].includes(setter.role)) {
    return { allowed: false, lockedBy: setter };
  }
  return { allowed: true, lockedBy: null };
}

// Gate D47 — what this branch makes that nobody has priced.
//
// ── Why this list exists, and why it is a LIST rather than a block ──
//
// A made item with no cost to make is worth nothing on paper, and everything
// built from it inherits that: a production run records a ledger row with no
// cost, and anything sent to another branch arrives valued at nothing. It is
// the one honest gap D45 leaves — D45 decided WHICH number to use, and this is
// what happens when that number was never set.
//
// SAP's answer is a material status that keeps the thing out of the flow until
// somebody sets a price. Stede's version is softer on purpose, and D41 is the
// reason: refusing to record a run that really happened leaves the STOCK COUNT
// wrong, and the count is what people work from. So the item stays usable and
// the gap is made impossible to miss instead.
//
// PAIRED WITH A WARNING AT SHIP TIME, which is the other half of D47. Until
// now the only warning was at RECORD time, so somebody who recorded a run in
// August could send the goods in September with nothing said.
//
// Sorted by name for the same reason the picker is: Houston makes 25 things
// and most of them start with the same word.
export function unpricedMakeables({ items = [], categories = [], branchId }) {
  return makeableAtBranch({ items, categories, branchId })
    .filter(i => num(i.standard_cost) == null);
}

// What a shipping line is about to be worth, and why it might be nothing.
//
// Returns null when there is nothing to warn about. Otherwise it names the
// REASON, because the two causes need different sentences and different people
// to fix them:
//
//   'no_cost_to_make'  a made thing nobody has priced  → a manager sets it
//   'no_branch_average' something bought that this branch has no average for
//                      → it settles when an invoice is reconciled
//
// It deliberately does NOT block. Gate D41's reasoning, restated: a wrong
// count is worse than an unvalued row, and demanding a number from somebody
// who does not know one invites a zero — which is a claim the thing was free,
// and is what the 79.6 defect actually was.
export function shipmentValueWarning({ fulfilmentMode, unitCost, itemName }) {
  if (num(unitCost) != null) return null;
  const made = fulfilmentMode === 'make';
  return {
    reason: made ? 'no_cost_to_make' : 'no_branch_average',
    itemName: itemName || 'This item',
    made,
  };
}
